import React from 'react';
import { AlertTriangle, Tag } from 'lucide-react';
import { inr, CalcLabel, CalcInput, StatusBadge } from './shared';

const GROUP_LABELS = { venue: 'Venue', event_spaces: 'Event Spaces', rooms: 'Accommodation', food: 'Food', addons: 'Add-Ons' };

export const StepReview = ({ result, selections, setSelections, onApplyDates }) => {
  if (!result) {
    return <p className="text-sm text-[#1A3626]/60 py-8 text-center" data-testid="calc-review-empty">Complete the previous steps to see your estimate.</p>;
  }
  const groups = ['venue', 'event_spaces', 'rooms', 'food', 'addons']
    .map(g => ({ key: g, lines: result.lines.filter(l => l.component === g) }))
    .filter(g => g.lines.length);

  return (
    <div className="space-y-5" data-testid="calc-step-review">
      {!result.availability?.available && (
        <div className="border border-amber-300 bg-amber-50 p-4" data-testid="calc-availability-warning">
          <p className="flex items-center gap-2 text-sm font-medium text-amber-800">
            <AlertTriangle className="w-4 h-4" /> Your selected dates are not available
          </p>
          <p className="text-xs text-amber-700 mt-1">Booked: {result.availability.conflicting_dates.join(', ')}</p>
          {(result.availability.suggested_dates || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {result.availability.suggested_dates.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => onApplyDates(s)}
                  className="text-xs border border-amber-400 bg-white px-3 py-1.5 hover:bg-amber-100 transition-colors"
                  data-testid={`calc-suggested-date-${i}`}
                >
                  {s.check_in} → {s.check_out}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {groups.map(g => (
        <div key={g.key}>
          <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#C6A87C] mb-2">{GROUP_LABELS[g.key]}</p>
          <div className="border border-[#E5DCCF] bg-white divide-y divide-[#E5DCCF]/60">
            {g.lines.map((l, i) => (
              <div key={i} className="px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-[#0A1710] truncate">{l.label}{l.qty ? <span className="text-[#1A3626]/50"> · {l.qty} {l.unit || ''}</span> : null}</p>
                  {l.notes && <p className="text-[11px] text-[#1A3626]/50">{l.notes}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <StatusBadge status={l.status} />
                  <span className="text-sm font-medium text-[#0A1710] tabular-nums w-24 text-right">{l.status === 'included' ? '—' : inr(l.amount)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div>
        <CalcLabel>Promo code</CalcLabel>
        <div className="flex gap-2 max-w-xs">
          <CalcInput
            placeholder="e.g. WVC10"
            value={selections.promo_code || ''}
            onChange={e => setSelections({ ...selections, promo_code: e.target.value })}
            data-testid="calc-promo-input"
          />
          <span className="flex items-center text-[#C6A87C]"><Tag className="w-4 h-4" /></span>
        </div>
      </div>

      <div className="border border-[#1A3626]/20 bg-white" data-testid="calc-totals">
        <div className="px-4 py-3 space-y-1.5">
          <Row label="Subtotal" value={inr(result.totals.subtotal)} />
          {result.discounts.map((d, i) => <Row key={i} label={`Discount · ${d.label}`} value={`- ${inr(d.amount)}`} accent />)}
          {result.charges.map((c, i) => <Row key={i} label={c.label} value={inr(c.amount)} />)}
          {result.taxes.map((t, i) => <Row key={i} label={t.label} value={inr(t.amount)} />)}
          {result.deposits.map((d, i) => <Row key={i} label={`${d.label}${d.refundable ? ' (refundable)' : ''}`} value={inr(d.amount)} />)}
        </div>
        <div className="px-4 py-3 bg-[#1A3626] flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#C6A87C]">Estimated Total</span>
          <span className="font-wvc-display text-2xl text-white" data-testid="calc-grand-total">{inr(result.totals.grand_total)}</span>
        </div>
      </div>

      {result.warnings.length > 0 && (
        <ul className="space-y-1" data-testid="calc-warnings">
          {result.warnings.map((w, i) => (
            <li key={i} className="text-[11px] text-amber-700 flex gap-1.5"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {w}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Row = ({ label, value, accent }) => (
  <div className="flex items-center justify-between text-sm">
    <span className={accent ? 'text-[#C6A87C]' : 'text-[#1A3626]/70'}>{label}</span>
    <span className={`tabular-nums font-medium ${accent ? 'text-[#C6A87C]' : 'text-[#0A1710]'}`}>{value}</span>
  </div>
);
